import {
  AssetType,
  FireTVCampaignAutomationClient,
  PresignedUrlAction,
} from '@amzn/fire-tv-campaign-automation-service';
import { PLACEHOLDER_URLS } from 'src/constants';
import { AssetContextState } from 'src/contexts/AssetContext';
import { AssetStatus } from 'src/types';

import { getPresignedUrls } from '../clients/controlPlane/apiMethods';

const LOADING_PREVIEW_ASSET_STATES: AssetStatus[] = [AssetStatus.TRANSFORMATION_IN_PROGRESS];

/**
 * Get asset urls for the assets and the keys provided.
 * If key is not provided - returns back placeholder urls
 * else - gets the presigned urls for the keys
 * @param assets - Array of assets with the type and key provided
 * @param controlPlaneClient- Client instance for making API calls
 * @returns Promise that resolves when URLs are fetched for the keys provided.
 */
export const getAssetUrls = async (
  assets: { type: AssetType; key: string | undefined }[],
  controlPlaneClient: FireTVCampaignAutomationClient,
): Promise<Record<string, string>> => {
  // Split assets into those with and without keys
  const assetsWithKeys = assets.filter((asset) => asset.key);
  const assetsWithoutKeys = assets.filter((asset) => !asset.key);

  // Get placeholder URLs for assets without keys
  const placeholderUrls = Object.fromEntries(
    assetsWithoutKeys.map((asset) => [asset.type, PLACEHOLDER_URLS[asset.type]]),
  );

  // Get presigned URLs for assets with keys
  const presignedUrls = await getPresignedUrlsForAssets(
    assetsWithKeys.map((asset) => ({ type: asset.type, key: asset.key! })),
    controlPlaneClient,
  );
  // Combine both sets of URLs
  return { ...placeholderUrls, ...presignedUrls };
};

/**
 * Gets presigned URLs for assets with keys via batched API call
 * @param assetsWithKeys - Array of assets with their types and keys
 * @param controlPlaneClient - Client instance for making API calls
 * @returns Promise that resolves when URLs are updated
 */
export const getPresignedUrlsForAssets = async (
  assetsWithKeys: { type: AssetType; key: string }[],
  controlPlaneClient: FireTVCampaignAutomationClient,
): Promise<Record<string, string>> => {
  if (assetsWithKeys.length === 0) {
    return {};
  }
  const keys = assetsWithKeys.map((item) => item.key);
  const presignedUrlResponses = await getPresignedUrls(
    controlPlaneClient,
    PresignedUrlAction.GET,
    keys,
  );
  const presignedUrlMap = new Map(
    presignedUrlResponses.map((response) => [response.key, response.presignedUrl]),
  );

  // Map asset types to their presigned URLs
  return Object.fromEntries(
    assetsWithKeys.map((asset) => [
      asset.type,
      presignedUrlMap.get(asset.key) || PLACEHOLDER_URLS[asset.type],
    ]),
  );
};

/**
 * Get changed asset types by comparing current and previous asset states
 * @param current - Current state of all assets
 * @param previous - Previous state of all assets
 * @returns Array of asset types that have changed
 */
export function getChangedAssetTypes(
  current: AssetContextState,
  previous: AssetContextState,
): AssetType[] {
  return Object.keys(current).filter(
    (assetType) => current[assetType as AssetType]?.key !== previous[assetType as AssetType]?.key,
  ) as AssetType[];
}

/**
 * Utility to check if any assets are in loading states
 * @param assetStates - Current state of all assets
 * @returns Boolean indicating if any assets are in loading states
 * @description This is used to determine if we should show the loading spinner or not.
 * We should show the loading spinner if any asset is in loading state.
 * We should not show the loading spinner if there are no assets at all.
 * We should not show the loading spinner if there are no assets in loading state.
 */
export function areAnyAssetsInLoadingStates(assetStates: AssetContextState): boolean {
  return Object.values(assetStates).some(
    (asset) => asset.status && LOADING_PREVIEW_ASSET_STATES.includes(asset.status),
  );
}
